import {
  Box,
  CardContent,
  Divider,
  FormControl,
  FormLabel,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useEffect, useState } from 'react';

import { Loader } from '@/components/layout/loading';
import { NetworkInterfaceSelect } from '@/components/regions/networkInterfaceSelect';
import { RegionSelect } from '@/components/regions/regionSelect';
import { ResourceGroupSelect } from '@/components/regions/resourceGroupSelect';
import { VirtualMachineSizeSelect } from '@/components/regions/virtualMachineSizeSelect';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { TagsSection } from '@/components/tags/tagsSection';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { StepContainer } from '../../wizard/StepContainer';
import type { RestoreAzureVMState } from '../restoreAzureVMWizard';

export const AzureVmConfiguration = (props: StepProps<RestoreAzureVMState>) => {
  const [isNameValid, setIsNameValid] = useState(false);

  const setVMName = (vmName: string, isValid: boolean) => {
    props.setWizardState((state) => ({ ...state, vmName }));
    setIsNameValid(isValid);
  };
  const onKeepOriginalTagsChange = (keepOriginalTags: boolean) =>
    props.setWizardState((state) => ({ ...state, keepOriginalTags }));
  const onTagsChange = (tags: Record<string, string>) =>
    props.setWizardState((state) => ({ ...state, tags }));

  const onNextClick = async () => {
    props.setNextStep(props.currentStep.next?.[0]);
  };

  return (
    <StepContainer
      sx={{ padding: '0' }}
      stepperLabels={props.stepperLabels}
      stepperIndex={1}
      onBackClick={props.back}
      canGoNext={() =>
        isNameValid &&
        props.wizardState.vmName !== undefined &&
        props.wizardState.resourceGroup !== undefined &&
        props.wizardState.location !== undefined &&
        props.wizardState.networkInterface !== undefined &&
        props.wizardState.vmSize !== undefined
      }
      onNextClick={onNextClick}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
        />
      }
    >
      <Box className='mx-[40px] my-[24px]'>
        <Stack direction='row' gap='24px' className='mt-[20px] mb-[24px]'>
          <VmName
            cloudAccountId={props.wizardState.restoreAccount?.id}
            resourceGroup={props.wizardState.resourceGroup}
            vmName={props.wizardState.vmName ?? ''}
            onChange={setVMName}
          />
          <RegionSelect
            accountId={props.wizardState.restoreAccount?.id}
            cloudProvider={Provider.Azure}
            regionName={props.wizardState.location}
            onChange={(location) =>
              props.setWizardState((state) => ({ ...state, location }))
            }
          />
        </Stack>
        <Stack direction='row' gap='24px' className='mt-[20px] mb-[24px]'>
          <ResourceGroupSelect
            accountId={props.wizardState.restoreAccount?.id}
            resourceGroup={props.wizardState.resourceGroup}
            onChange={(resourceGroup) =>
              props.setWizardState((state) => ({
                ...state,
                resourceGroup,
              }))
            }
          />
          <NetworkInterfaceSelect
            accountId={props.wizardState.restoreAccount?.id}
            resourceGroup={props.wizardState.resourceGroup}
            networkInterface={props.wizardState.networkInterface}
            onChange={(networkInterface) =>
              props.setWizardState((state) => ({
                ...state,
                networkInterface,
              }))
            }
          />
        </Stack>
        <Stack direction='row' gap='24px' className='mt-[20px] mb-[24px]'>
          <VirtualMachineSizeSelect
            vmSize={props.wizardState.vmSize}
            disksState={props.wizardState.disksState}
            originalVMSize={props.wizardState.originalVMSize}
            accountId={props.wizardState.restoreAccount?.id}
            location={props.wizardState.location}
            onChange={(vmSize) =>
              props.setWizardState((state) => ({ ...state, vmSize }))
            }
          />
        </Stack>
      </Box>
      <Divider />
      <CardContent className='px-0'>
        <TagsSection
          initialTags={props.wizardState.initialTags ?? {}}
          tags={props.wizardState.tags ?? {}}
          keepOriginalTags={props.wizardState.keepOriginalTags}
          setKeepOriginalTags={onKeepOriginalTagsChange}
          onChange={onTagsChange}
        />
      </CardContent>
    </StepContainer>
  );
};

const VmName = ({
  cloudAccountId,
  resourceGroup,
  vmName,
  onChange,
}: {
  cloudAccountId?: string;
  resourceGroup?: string;
  vmName: string;
  onChange: (value: string, isValid: boolean) => void;
}) => {
  const [value, setValue] = useState(vmName);
  const [error, setError] = useState('');
  const [debouncedValue, setDebouncedValue] = useState(vmName);
  const dal = useDAL();
  const { body, isLoading } = dal.restore.azureVm.checkNameExists(
    cloudAccountId,
    resourceGroup,
    debouncedValue
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, 200);

    return () => {
      clearTimeout(handler);
    };
  }, [value]);

  useEffect(() => {
    if (!isLoading && body?.exists) {
      setError('VM name already exists');
    } else {
      setError('');
    }
  }, [body, isLoading]);

  useEffect(() => {
    onChange(debouncedValue, !error && body?.exists === false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [body, debouncedValue, error]);

  return (
    <FormControl size='small' className='flex-1'>
      <FormLabel>VM Name*</FormLabel>
      <TextField
        value={value}
        onChange={(e) => setValue(e.target.value)}
        error={Boolean(error)}
        helperText={error}
        inputProps={{
          maxLength: 80,
        }}
        InputProps={{
          endAdornment: isLoading ? (
            <InputAdornment position='end'>
              <Loader variant='inline' />
            </InputAdornment>
          ) : !value ? undefined : body ? (
            body.exists ? (
              <InputAdornment position='end'>
                <i className='clarity-error-line text-[22px]' />
              </InputAdornment>
            ) : (
              <InputAdornment position='end'>
                <i className='material-symbols-check-circle-outline-rounded' />
              </InputAdornment>
            )
          ) : undefined,
        }}
      />
    </FormControl>
  );
};
